import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import AlertBanner from '@/components/common/AlertBanner';
import Checkbox from '@/components/Forms/Checkbox';
import SelectInput from '@/components/Forms/SelectInput';
import TextInput from '@/components/Forms/TextInput';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import StaffLayout from '@/layouts/StaffLayout';
import { Academy, User } from '@/types';
import { Link, router, useForm } from '@inertiajs/react';
import { Trash2 } from 'lucide-react';
import React, { useState } from 'react';

interface EditProps {
    user: User;
    academies: Academy[];
    errors?: Record<string, string>;
}

const EditContent: React.FC<EditProps> = ({
    user,
    academies,
    errors: serverErrors,
}) => {
    const { route } = useRoutes();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const { data, setData, post, processing, errors } = useForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        academy_id: user.current_academy?.id || '',
        is_active: user.is_active,
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        post(
            route('staff-commercial-users-edit', {
                user_id: user.id,
            }).toString()
        );
    };

    const handleDelete = () => {
        router.post(
            route('staff-commercial-users-delete', {
                user_id: user.id,
            }).toString(),
            {},
            {
                onSuccess: () => {
                    router.visit(route('staff-commercial-users').toString());
                },
            }
        );
    };

    const allErrors = { ...serverErrors, ...errors };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-3xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Editar Usuario Comercial
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-neutral-300">
                        Modifica la información del usuario
                    </p>
                </div>

                <div className="rounded-lg bg-white p-6 shadow dark:bg-neutral-800">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Email (read-only) */}
                        <TextInput
                            label="Email"
                            name="email"
                            type="email"
                            value={user.email}
                            onChange={() => {}}
                            disabled
                            helpText="El email no puede ser modificado"
                        />

                        {/* First Name */}
                        <TextInput
                            label="Nombre"
                            name="first_name"
                            value={data.first_name}
                            onChange={(v) => setData('first_name', v)}
                            error={allErrors.first_name}
                        />

                        {/* Last Name */}
                        <TextInput
                            label="Apellido"
                            name="last_name"
                            value={data.last_name}
                            onChange={(v) => setData('last_name', v)}
                            error={allErrors.last_name}
                        />

                        {/* Academy */}
                        <SelectInput
                            label="Academia"
                            name="academy_id"
                            value={data.academy_id}
                            onChange={(v) => setData('academy_id', Number(v))}
                            error={allErrors.academy_id}
                        >
                            <option value="">Selecciona una academia</option>
                            {academies.map((academy) => (
                                <option key={academy.id} value={academy.id}>
                                    {academy.name}
                                </option>
                            ))}
                        </SelectInput>

                        {/* Is Active */}
                        <div>
                            <Checkbox
                                name="is_active"
                                label="Usuario activo"
                                checked={data.is_active}
                                onChange={(checked) =>
                                    setData('is_active', checked)
                                }
                            />
                            <p className="mt-1 ml-6 text-xs text-gray-500 dark:text-neutral-400">
                                Los usuarios inactivos no podrán iniciar sesión
                            </p>
                        </div>

                        {allErrors.general && (
                            <AlertBanner variant="error">
                                {allErrors.general}
                            </AlertBanner>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between border-t pt-4">
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(true)}
                                className="inline-flex items-center rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:bg-neutral-800 dark:text-red-400"
                            >
                                <Trash2 size={16} className="mr-2" />
                                Desactivar Usuario
                            </button>

                            <div className="flex space-x-3">
                                <Link
                                    href={route(
                                        'staff-commercial-users'
                                    ).toString()}
                                >
                                    <SecondaryButton type="button">
                                        Cancelar
                                    </SecondaryButton>
                                </Link>
                                <PrimaryButton
                                    type="submit"
                                    disabled={processing}
                                >
                                    {processing
                                        ? 'Guardando...'
                                        : 'Guardar Cambios'}
                                </PrimaryButton>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
                    <div className="mx-4 max-w-md rounded-lg bg-white p-6 dark:bg-neutral-800">
                        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                            ¿Desactivar usuario?
                        </h3>
                        <p className="mb-6 text-sm text-gray-600 dark:text-neutral-300">
                            Esta acción desactivará al usuario{' '}
                            <strong>{user.full_name}</strong>. El usuario no
                            podrá iniciar sesión pero sus datos se conservarán.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <SecondaryButton
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancelar
                            </SecondaryButton>
                            <button
                                onClick={handleDelete}
                                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                            >
                                Desactivar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </StaffLayout>
    );
};

const Edit: React.FC<EditProps> = (props) => {
    return (
        <ThemeWrapper>
            <EditContent {...props} />
        </ThemeWrapper>
    );
};

export default Edit;
